// Phase 1 (Founder-First UX) -- shared state for every founder surface:
// which workspace is open, what needs the founder, and what Ask Martian is
// currently looking at. One provider, mounted once by FounderShell, so the
// six surfaces never disagree about the workspace or the counts in the nav.
//
// Security: this only decides what the UI shows. Every request still sends
// its own explicit client id and is verified server-side
// (get_verified_client) -- nothing here widens access.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clientsAPI, decisionsAPI } from '@/lib/api';

const FounderWorkspaceContext = createContext(null);

const STORAGE_KEY = 'martian_workspace_id';

function readStoredWorkspace() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function storeWorkspace(id) {
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Storage unavailable (private window) -- the choice just isn't remembered.
  }
}

export function FounderWorkspaceProvider({ children, fixedClientId, basePath = '' }) {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [workspaceId, setWorkspaceIdState] = useState(fixedClientId || '');
  const [summary, setSummary] = useState(null); // decisions payload; null = loading
  const [summaryError, setSummaryError] = useState(null);
  const [askOpen, setAskOpen] = useState(false);
  const [askContext, setAskContext] = useState(null);

  useEffect(() => {
    let cancelled = false;
    clientsAPI.list()
      .then((res) => {
        if (cancelled) return;
        const list = res.data || [];
        setClients(list);
        if (fixedClientId) return;
        const stored = readStoredWorkspace();
        const pick = list.find((c) => c.id === stored) || list[0];
        if (pick) setWorkspaceIdState(pick.id);
      })
      .catch(() => { if (!cancelled) setClients([]); })
      .finally(() => { if (!cancelled) setLoadingClients(false); });
    return () => { cancelled = true; };
  }, [fixedClientId]);

  const setWorkspaceId = useCallback((id) => {
    setWorkspaceIdState(id);
    storeWorkspace(id);
    setSummary(null);
    setAskContext(null);
  }, []);

  const refreshSummary = useCallback(() => {
    if (!workspaceId) return Promise.resolve();
    setSummaryError(null);
    return decisionsAPI.list(workspaceId)
      .then((res) => setSummary(res.data))
      .catch((err) => { setSummaryError(err); setSummary({ decisions: [], needs_you: 0 }); });
  }, [workspaceId]);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  const client = useMemo(() => clients.find((c) => c.id === workspaceId) || null, [clients, workspaceId]);
  const displayName = client?.display_name || client?.name || summary?.brand || '';

  const openAsk = useCallback((context) => {
    if (context !== undefined) setAskContext(context);
    setAskOpen(true);
  }, []);

  const value = {
    clients, loadingClients, workspaceId, setWorkspaceId, client, displayName,
    fixedClientId: fixedClientId || null,
    basePath,
    summary, summaryError, refreshSummary,
    askOpen, setAskOpen, askContext, setAskContext, openAsk,
  };
  return <FounderWorkspaceContext.Provider value={value}>{children}</FounderWorkspaceContext.Provider>;
}

export function useFounderWorkspace() {
  const ctx = useContext(FounderWorkspaceContext);
  if (!ctx) throw new Error('useFounderWorkspace must be used inside FounderWorkspaceProvider');
  return ctx;
}

// Founder paths are relative to the workspace: '' for a brand owner,
// '/workspace/:id' when an admin has entered a verified workspace.
export function useFounderPath() {
  const { basePath } = useFounderWorkspace();
  return useCallback((path) => {
    if (!basePath) return path;
    return path === '/' ? basePath : `${basePath}${path}`;
  }, [basePath]);
}

// Counts that are real outstanding actions (Zeigarnik) -- never invented.
export function attentionCounts(summary) {
  const decisions = summary?.decisions || [];
  const readyWork = decisions.filter((d) => d.needs_from_you?.action === 'review_work').length;
  return { decisions: summary?.needs_you || 0, work: readyWork };
}
